/**
 * yield-curve — the pure half of the `yieldCurve` macro section
 * (`docs/plans/macro.md`; behaviour spec `docs/mockups/macro-v2.html`, section 02).
 *
 * Everything the section draws runs on **one weekly clock**: the curve's weeks.
 * A cursor is an index into them, and every panel — the curve, the bp table, the
 * spread strips, the heat strip and each linked chart — reads that index. Bars
 * and macro series are aligned onto the same weeks here, once, so a panel never
 * has to search by date while the cursor moves.
 *
 * No React, no fetch. The section (`components/macro/YieldCurveSection.tsx`)
 * holds the view state (cursor, hover, playback — D8, never stored) and the
 * client (`api/macro-curve.ts`) supplies the data.
 */

import type { YieldCurveConfig } from "@qufolio/model";

/**
 * The stored constant-maturity tenors, in order — `macro_store`'s
 * `us.treasury.yield_*` fields (`mv/macro.YIELD_FIELDS`). The mock drew eleven;
 * the store holds these seven, so the curve draws seven.
 */
export const TENORS = ["1M", "3M", "1Y", "2Y", "5Y", "10Y", "30Y"] as const;
export type Tenor = (typeof TENORS)[number];

/** One week of the curve: the week's last observation, `NaN` where a tenor is absent. */
export interface CurveWeek {
  /** UTC milliseconds of the observation the week was sampled from. */
  t: number;
  /** Percent, one per {@link TENORS} entry. */
  y: number[];
}

/** One daily (or weekly) observation as a client receives it. */
export interface CurveRow {
  date: string;
  y: (number | null)[];
}

export const WEEK_MS = 7 * 86_400_000;

/** `2026-10-30` → UTC ms at midnight. Parsed as UTC, never local (`chart-window.ts`'s off-by-one). */
export function utc(date: string): number {
  return Date.parse(`${date.slice(0, 10)}T00:00:00Z`);
}

export function isoDay(t: number): string {
  return new Date(t).toISOString().slice(0, 10);
}

/** The Monday-anchored week a UTC ms falls in (1970-01-01 was a Thursday). */
function weekKey(t: number): number {
  return Math.floor((t + 3 * 86_400_000) / WEEK_MS);
}

/**
 * Daily rows → one row per week, the week's **last** observation. Rows may come
 * unordered; tenors absent on a day are `NaN`. A week whose every tenor is absent
 * is dropped rather than drawn as a gap in the clock.
 */
export function weekly(rows: readonly CurveRow[]): CurveWeek[] {
  const sorted = [...rows].sort((a, b) => utc(a.date) - utc(b.date));
  const out: CurveWeek[] = [];
  let lastKey: number | null = null;
  for (const r of sorted) {
    const t = utc(r.date);
    if (!Number.isFinite(t)) continue;
    const y = TENORS.map((_, i) => {
      const v = r.y[i];
      return typeof v === "number" && Number.isFinite(v) ? v : NaN;
    });
    if (y.every((v) => Number.isNaN(v))) continue;
    const k = weekKey(t);
    if (k === lastKey) out[out.length - 1] = { t, y };
    else out.push({ t, y });
    lastKey = k;
  }
  return out;
}

/** An indexed number, `NaN` where the index is out of range (`noUncheckedIndexedAccess`). */
function num(v: number | undefined): number {
  return v ?? NaN;
}

export function tenorIndex(t: Tenor): number {
  return TENORS.indexOf(t);
}

/** The spreads a strip can show, long leg first. */
export const SPREADS: Record<string, readonly [Tenor, Tenor]> = {
  "10Y − 2Y": ["10Y", "2Y"],
  "10Y − 3M": ["10Y", "3M"],
  "30Y − 5Y": ["30Y", "5Y"],
  "5Y − 2Y": ["5Y", "2Y"],
  "2Y − 3M": ["2Y", "3M"],
  "30Y − 10Y": ["30Y", "10Y"],
};
export type SpreadKey = keyof typeof SPREADS;
export const DEFAULT_SPREADS: readonly [SpreadKey, SpreadKey] = ["10Y − 2Y", "10Y − 3M"];

export function spreadAt(week: CurveWeek, key: SpreadKey): number {
  const [a, b] = SPREADS[key] ?? (["10Y", "2Y"] as const);
  return num(week.y[tenorIndex(a)]) - num(week.y[tenorIndex(b)]);
}

/** 10Y below 2Y: the curve is drawn in the negative colour and the state pill says so. */
export function isInverted(week: CurveWeek): boolean {
  return num(week.y[tenorIndex("10Y")]) < num(week.y[tenorIndex("2Y")]);
}

/** Consecutive weeks up to and including `cursor` that `key` has been below zero. */
export function invertedStreak(weeks: readonly CurveWeek[], cursor: number, key: SpreadKey): number {
  let n = 0;
  for (let j = cursor; j >= 0 && j < weeks.length && spreadAt(weeks[j]!, key) < 0; j--) n++;
  return n;
}

// ── The window, the pin, the playback ──────────────────────────────────────

export type WindowSetting = NonNullable<YieldCurveConfig["window"]>;
export type PinSetting = NonNullable<YieldCurveConfig["pin"]>;

/** Weeks of history a Follow window holds. */
export const WINDOW_WEEKS: Record<Exclude<WindowSetting, "all">, number> = { "2y": 104, "5y": 260 };

/**
 * The visible index range for a cursor. **Follow** keeps a quarter of the window
 * ahead of the cursor in view (6 months at 2Y, 15 months at 5Y), so playback
 * scrolls with the cursor and the next stretch is visible — faint or hidden.
 */
export function viewDomain(win: WindowSetting, cursor: number, last: number): { lo: number; hi: number } {
  if (win === "all" || last <= 0) return { lo: 0, hi: Math.max(0, last) };
  const span = WINDOW_WEEKS[win];
  const ahead = Math.round(span / 4);
  let hi = Math.min(last, cursor + ahead);
  const lo = Math.max(0, hi - span);
  hi = Math.min(last, lo + span);
  return { lo, hi };
}

/** How far back each pin reaches, in weeks. `thisWeek` pins the cursor itself. */
export const PIN_WEEKS: Record<Exclude<PinSetting, "off">, number> = { "1m": 4, "1y": 52, "2y": 104, thisWeek: 0 };

/**
 * The pinned week for a setting, measured from the cursor **when the pin is
 * set** — the pin then stays put while the cursor moves (the mock's behaviour:
 * 📌 1Y earlier, then ▶ Play, watches today's curve move away from it).
 */
export function pinIndex(pin: PinSetting, cursor: number): number | null {
  if (pin === "off") return null;
  return Math.max(0, cursor - PIN_WEEKS[pin]);
}

/** One step of playback: the next cursor, or `null` at the end. */
export function playStep(cursor: number, last: number): number | null {
  return cursor >= last ? null : cursor + 1;
}

/** Playback's tick at a speed: 400 ms per week at 1×. */
export function playIntervalMs(speed: number): number {
  return Math.round(400 / Math.max(1, speed));
}

/** Pointer x (px inside a panel) → week index, clamped, for a panel plotting `lo..hi` across `left..right`. */
export function indexAtX(px: number, left: number, right: number, lo: number, hi: number, last: number): number {
  const f = right > left ? (px - left) / (right - left) : 0;
  return Math.max(0, Math.min(last, Math.round(lo + f * (hi - lo))));
}

// ── Aligning bars and series onto the weeks ────────────────────────────────

export interface WeeklyBar {
  /** UTC ms of the bar. */
  t: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface AlignedBars {
  open: number[];
  high: number[];
  low: number[];
  close: number[];
}

/**
 * Bars → one per curve week: the last bar whose (Monday-anchored) week is no
 * later than the curve week's. Compared by week rather than by timestamp, so a
 * weekly bar stamped at the week's start and a curve sampled on its Friday
 * still meet — and next week's bar, stamped the following Monday, never leaks
 * back. `NaN` before the instrument's first bar, and more than two weeks past
 * its last, so a delisted or late series is a gap rather than a flat line.
 */
export function alignBars(weeks: readonly CurveWeek[], bars: readonly WeeklyBar[]): AlignedBars {
  const sorted = [...bars].sort((a, b) => a.t - b.t);
  const out: AlignedBars = { open: [], high: [], low: [], close: [] };
  let j = -1;
  for (const w of weeks) {
    const wk = weekKey(w.t);
    while (j + 1 < sorted.length && weekKey(sorted[j + 1]!.t) <= wk) j++;
    const at = j >= 0 ? sorted[j] : undefined;
    const b = at && wk - weekKey(at.t) <= 2 ? at : null;
    out.open.push(b ? b.open : NaN);
    out.high.push(b ? b.high : NaN);
    out.low.push(b ? b.low : NaN);
    out.close.push(b ? b.close : NaN);
  }
  return out;
}

/** A step series (a policy rate) → its value on each week: the last change on or before it. */
export function alignSteps(weeks: readonly CurveWeek[], steps: readonly { date: string; value: number }[]): number[] {
  const sorted = [...steps].map((s) => ({ t: utc(s.date), v: s.value })).sort((a, b) => a.t - b.t);
  let j = -1;
  return weeks.map((w) => {
    while (j + 1 < sorted.length && sorted[j + 1]!.t <= w.t) j++;
    return j >= 0 ? sorted[j]!.v : NaN;
  });
}

// ── The linked charts ──────────────────────────────────────────────────────

/** A rate a linked chart can be paired with, read off the curve. */
export const PAIRS = ["10Y", "2Y", "30Y", "10Y − 2Y", "10Y − 3M", "(none)"] as const;
export type PairKey = (typeof PAIRS)[number];

export function pairAt(week: CurveWeek, pair: PairKey): number {
  if (pair === "(none)") return NaN;
  if (pair in SPREADS) return spreadAt(week, pair as SpreadKey);
  return num(week.y[tenorIndex(pair as Tenor)]);
}

export interface LaneDef {
  key: string;
  label: string;
  /** `px` lanes are bars (candles when there is room); `mac` lanes are a line. */
  kind: "px" | "mac";
  /** Theme token for the lane's own colour. */
  color: string;
  pair: PairKey;
  /** Draw the paired rate upside down, for an inverse link. */
  inverted: boolean;
  why: string;
}

/**
 * The lanes on offer. Rates come off the curve, so a lane's pair is always real
 * once the curve is; Fed Funds is the one macro lane until the other macro
 * feeds have a route (see `api/macro-curve.ts`).
 */
export const LANES: readonly LaneDef[] = [
  { key: "SPY", label: "SPY", kind: "px", color: "series1", pair: "10Y", inverted: false, why: "The index against the long rate" },
  { key: "QQQ", label: "QQQ", kind: "px", color: "accent.blue", pair: "10Y", inverted: true, why: "Long-duration growth, hurt by higher rates" },
  { key: "IWM", label: "IWM", kind: "px", color: "series5", pair: "2Y", inverted: true, why: "Small caps carry floating-rate debt" },
  { key: "GLD", label: "GLD", kind: "px", color: "accent.orange", pair: "10Y", inverted: true, why: "Gold pays nothing, so it competes with yield" },
  { key: "TLT", label: "TLT", kind: "px", color: "accent.purple", pair: "30Y", inverted: true, why: "Roughly the long yield upside down" },
  { key: "KRE", label: "KRE", kind: "px", color: "series3", pair: "10Y − 3M", inverted: false, why: "Banks earn on the steepness" },
  { key: "FEDFUNDS", label: "Fed Funds", kind: "mac", color: "neutral", pair: "2Y", inverted: false, why: "The 2Y moves ahead of the Fed · target, upper bound" },
];

/** SPY vs 10Y first (macro.md, *Curve + markets*). */
export const DEFAULT_LANES: readonly string[] = ["SPY", "GLD", "TLT", "FEDFUNDS"];

/** The config's lanes, unknown keys dropped, duplicates removed; absent → the default. */
export function lanesOf(config: YieldCurveConfig): string[] {
  const known = new Set(LANES.map((l) => l.key));
  const asked = config.lanes ?? DEFAULT_LANES;
  return [...new Set(asked.filter((k) => known.has(k)))];
}

/** Toggle a lane, keeping the offered order (so SPY stays first when it is on). */
export function toggleLane(on: readonly string[], key: string): string[] {
  const set = new Set(on);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  return LANES.map((l) => l.key).filter((k) => set.has(k));
}

/** Pearson r, `null` when either side has no variance or fewer than 20 points. */
export function correlation(xs: readonly number[], ys: readonly number[]): number | null {
  const n = Math.min(xs.length, ys.length);
  const a: number[] = [];
  const b: number[] = [];
  for (let i = 0; i < n; i++) {
    const xv = num(xs[i]);
    const yv = num(ys[i]);
    if (Number.isFinite(xv) && Number.isFinite(yv)) {
      a.push(xv);
      b.push(yv);
    }
  }
  if (a.length < 20) return null;
  const mean = (v: number[]) => v.reduce((s, x) => s + x, 0) / v.length;
  const ma = mean(a);
  const mb = mean(b);
  let sab = 0;
  let saa = 0;
  let sbb = 0;
  for (let i = 0; i < a.length; i++) {
    const da = a[i]! - ma;
    const db = b[i]! - mb;
    sab += da * db;
    saa += da * da;
    sbb += db * db;
  }
  return saa && sbb ? sab / Math.sqrt(saa * sbb) : null;
}

/**
 * ρ52w: the correlation of the last 52 weekly **changes** up to the cursor —
 * returns for a price lane, differences for a macro lane, differences of the
 * paired rate. Not flipped by ⇅: the flip is how it is drawn, not what it is.
 */
export function rho52(
  values: readonly number[],
  kind: LaneDef["kind"],
  pair: readonly number[],
  cursor: number,
): number | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let j = Math.max(1, cursor - 51); j <= cursor; j++) {
    const v1 = num(values[j]);
    const v0 = num(values[j - 1]);
    xs.push(kind === "px" ? v1 / v0 - 1 : v1 - v0);
    ys.push(num(pair[j]) - num(pair[j - 1]));
  }
  return correlation(xs, ys);
}

/** Since the pin: % for a price, bp for a rate; `null` with no pin or no data. */
export function sinceChange(
  values: readonly number[],
  kind: LaneDef["kind"],
  pin: number | null,
  cursor: number,
): { value: number; unit: "%" | "bp" } | null {
  if (pin == null || pin === cursor) return null;
  const a = num(values[pin]);
  const b = num(values[cursor]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return kind === "px" ? { value: (b / a - 1) * 100, unit: "%" } : { value: (b - a) * 100, unit: "bp" };
}

/** Change in basis points, cursor against pin, per tenor. */
export function bpChange(cur: CurveWeek, pin: CurveWeek): number[] {
  return cur.y.map((v, i) => Math.round((v - num(pin.y[i])) * 100));
}

// ── Events ──────────────────────────────────────────────────────────────────

/** Curve and Fed events marked ◆ on the first spread strip; names on hover. */
export const EVENTS: readonly (readonly [string, string])[] = [
  ["2019-08-14", "2s10s inverts"],
  ["2020-03-15", "Fed to zero · COVID"],
  ["2022-03-16", "First hike"],
  ["2022-07-05", "2s10s inverts"],
  ["2023-03-10", "SVB fails"],
  ["2023-07-26", "Last hike · 5.50%"],
  ["2024-09-06", "2s10s un-inverts"],
  ["2024-09-18", "First cut −50"],
  ["2025-12-10", "Cut to 3.50–3.75%"],
  ["2026-09-16", "Hike to 3.75–4.00%"],
  ["2026-10-28", "Fed holds 3.75–4.00%"],
];

/** Events within two weeks of `t`, for the "Week of" tile. */
export function eventsNear(t: number): string[] {
  return EVENTS.filter(([d]) => Math.abs(utc(d) - t) <= 2 * WEEK_MS).map(([, n]) => n);
}

/** The first week on or after each event, for the ◆ marks. */
export function eventIndices(weeks: readonly CurveWeek[]): { index: number; date: string; name: string }[] {
  const out: { index: number; date: string; name: string }[] = [];
  for (const [date, name] of EVENTS) {
    const t = utc(date);
    const index = weeks.findIndex((w) => w.t >= t);
    if (index >= 0) out.push({ index, date, name });
  }
  return out;
}

// ── Axis ticks ─────────────────────────────────────────────────────────────

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Gridlines at each new year, and each new quarter when the span is short enough to read them. */
export function timeTicks(weeks: readonly CurveWeek[], lo: number, hi: number): { index: number; label: string; year: boolean }[] {
  const out: { index: number; label: string; year: boolean }[] = [];
  const span = hi - lo;
  const yearEvery = span > 1200 ? 5 : span > 520 ? 2 : 1;
  let py: number | null = null;
  let pq: number | null = null;
  for (let i = lo; i <= hi; i++) {
    const d = new Date(weeks[i]!.t);
    const y = d.getUTCFullYear();
    const q = Math.floor(d.getUTCMonth() / 3);
    const newY = y !== py;
    const newQ = q !== pq;
    if (i > lo && newY && y % yearEvery === 0) out.push({ index: i, label: String(y), year: true });
    else if (i > lo && !newY && newQ && span <= 130) out.push({ index: i, label: MON[q * 3] ?? "", year: false });
    py = y;
    pq = q;
  }
  return out;
}

// ── Colour ─────────────────────────────────────────────────────────────────

function hexRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
  const n = parseInt(full, 16);
  return Number.isFinite(n) ? [(n >> 16) & 255, (n >> 8) & 255, n & 255] : [128, 128, 128];
}

/** A theme colour with an alpha, as `rgba()`. */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * The top of the heat strip's ramp, in percent: fixed at 6, as the mock draws
 * it (`docs/mockups/macro-v2.html`, `heat()`), so a yield at or above 6% is the
 * ramp's last colour. Not the history's max — back to 1962 that is the 1981
 * peak, and every week since 2008 would fall into the bottom third of the ramp.
 */
export const HEAT_MAX = 6;

/**
 * The heat strip's colour for a yield: a ramp through three theme colours
 * (low → mid → high) over `0..hi` percent. Tokens in, so a theme switch repaints
 * the strip in that theme's own colours (D13).
 */
export function heatColor(v: number, hi: number, ramp: readonly [string, string, string]): string {
  if (!Number.isFinite(v)) return "rgba(0,0,0,0)";
  const t = Math.max(0, Math.min(1, v / Math.max(0.01, hi)));
  const [a, b] = t < 0.5 ? [ramp[0], ramp[1]] : [ramp[1], ramp[2]];
  const f = t < 0.5 ? t * 2 : (t - 0.5) * 2;
  const ca = hexRgb(a);
  const cb = hexRgb(b);
  const mix = ca.map((c, i) => Math.round(c + (cb[i]! - c) * f));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}

/** `+0.42` / `-0.18` — a spread, signed. */
export function signed(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return "—";
  return (v >= 0 ? "+" : "") + v.toFixed(digits);
}

/** min/max over finite values in `lo..hi`, ignoring `NaN`; `null` when there are none. */
export function finiteRange(values: readonly number[], lo: number, hi: number): [number, number] | null {
  let mn = Infinity;
  let mx = -Infinity;
  for (let i = lo; i <= hi; i++) {
    const v = values[i];
    if (v != null && Number.isFinite(v)) {
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
  }
  return mn <= mx ? [mn, mx] : null;
}

/**
 * The Fed's target range from its upper bound: 25bp wide (every move since
 * Dec 2008), floored at zero. Null when the upper bound is unknown.
 */
export function fedRange(upper: number | null | undefined): { lo: number; hi: number; label: string } | null {
  if (upper == null || !Number.isFinite(upper)) return null;
  const lo = Math.max(0, Math.round((upper - 0.25) * 100) / 100);
  return { lo, hi: upper, label: `${lo.toFixed(2)}–${upper.toFixed(2)}` };
}
